import { Textarea } from '@ifixit/ui/components/textarea';
import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { useLineDoc } from './use_guide_edit_api';

/**
 * The subset of KeyboardEvent the line shortcuts read; both React's
 * synthetic events and ProseMirror's native ones satisfy it.
 */
export type LineShortcutEvent = Pick<
   KeyboardEvent,
   'key' | 'code' | 'ctrlKey' | 'altKey' | 'metaKey' | 'shiftKey'
> & { preventDefault: () => void };

export type LineEditorProps = {
   /**
    * The line's committed wikitext when editing began. Keys the server
    * parse that seeds the WYSIWYG; stays fixed for the edit session.
    */
   initialText: string;
   /** The live draft (raw wikitext); parent-owned. */
   value: string;
   onChange: (text: string) => void;
   onFinish: () => void;
   /** Line shortcuts (Ctrl+Alt+N/P, Ctrl+P/D, …); true when the key was consumed. */
   onShortcutKey: (event: LineShortcutEvent) => boolean;
   /** Line actions leading the toolbar, before the format buttons. */
   toolbarStart?: ReactNode;
   /** Line actions, rendered after the format buttons. */
   toolbar: ReactNode;
   /** The character counter, rendered right of the toolbar. */
   counter: ReactNode;
};

// Tiptap and its extensions stay out of the eager entry chunk; this shares
// the split point the intro/conclusion editors already pay for.
const WysiwygEditor = lazy(() => import('./LineWysiwygEditor'));

/**
 * One step line's editor: WYSIWYG over the server-parsed doc. While the
 * parse is in flight the raw text shows in a placeholder box, and if the
 * parse fails the line is still editable as plain wikitext.
 */
export function LineWysiwyg(props: LineEditorProps) {
   const doc = useLineDoc(props.initialText);

   if (doc.isError) {
      return <PlainLineEditor {...props} />;
   }
   if (doc.data === undefined) {
      return <LinePendingBox text={props.value} />;
   }
   return (
      <Suspense fallback={<LinePendingBox text={props.value} />}>
         <WysiwygEditor
            doc={doc.data}
            initialText={props.initialText}
            onChange={props.onChange}
            onFinish={props.onFinish}
            onShortcutKey={props.onShortcutKey}
            toolbarStart={props.toolbarStart}
            toolbar={props.toolbar}
            counter={props.counter}
         />
      </Suspense>
   );
}

/** The editing box's shape while the doc or the editor chunk loads. */
function LinePendingBox({ text }: { text: string }) {
   return (
      <div className="min-w-0 flex-1">
         <div className="min-h-9 rounded-lg border border-gray-300 bg-gray-50 px-2 py-1 text-sm text-gray-500">
            {text}
         </div>
      </div>
   );
}

/**
 * Raw-wikitext fallback when the parse endpoint fails: no formatting
 * buttons, but the line stays editable and the line actions still work.
 */
function PlainLineEditor({
   value,
   onChange,
   onFinish,
   onShortcutKey,
   toolbarStart,
   toolbar,
   counter,
}: LineEditorProps) {
   return (
      <div className="min-w-0 flex-1">
         <Textarea
            autoFocus
            value={value}
            onChange={event => onChange(event.target.value)}
            onBlur={onFinish}
            onKeyDown={event => {
               if (onShortcutKey(event)) {
                  return;
               }
               if (!(event.key === 'Escape' || (event.key === 'Enter' && !event.shiftKey))) {
                  return;
               }
               event.preventDefault();
               onFinish();
            }}
         />
         <div className="flex items-center justify-between gap-2">
            <div
               className="flex flex-wrap items-center gap-1"
               onMouseDown={event => event.preventDefault()}
            >
               {toolbarStart}
               {toolbar}
            </div>
            {counter}
         </div>
      </div>
   );
}
